import { setMessage } from '../../../store/reducers/appSlice';
import {
  setCurrentChatInfo,
  setLastUsedMCPs,
  setPreviousResponseId,
  setStreamingStatusMessages,
} from '../../../store/reducers/chatSlice';
import { setIsPaymentPopupOpen } from '../../../store/reducers/uiSlice';
import store from '../../../store/store';
import { APP_ENV, TARA_CHAT_STREAM_API } from '../../constants/central-api';
import isNotEmptyString from '../../helper-functions/isNotEmptyString';
import check_tara_can_chat_status_api_call from './check-tara-can-chat-status-api-call';
import get_tara_single_chat_api_call from './get_tara_single_chat_api_call';
import post_tara_error_log_api_call from './post_tara_error_log_api_call';
import tara_chat_history_api_call from './tara_chat_history_api_call';

/* ----------------Helper Function Start -------------------*/

// Utility: Simple delay using Promise (used between retries)
const wait = (ms: number) => new Promise((res) => setTimeout(res, ms));

/**
 * Attempts to recover from a failed stream by checking the latest chat from backend
 * and retrieving the most recent system message. Supports both new and existing chats.
 *
 * @param retries Number of retry attempts (default: 3)
 * @param delay Delay between retries in milliseconds (default: 5000)
 */
async function tryFetchLatestChatMessageWithRetries(retries = 3, delay = 5000) {
  // Get current Redux state values
  const state = store.getState();
  const chatHistoryList = state.chat.allChatsHistory; // Local history (5 chats max)
  const currentChat = state.chat.currentChat;

  // Extract chatId safely
  const chatId = currentChat?.chatId;

  // Determine if this is a new chat (either no ID, starts with "temp", or doesn't start with "TaraAi_")
  const isNewChat =
    !chatId ||
    (typeof chatId === 'string' &&
      (chatId.startsWith('temp') || !chatId.startsWith('TaraAi_')));

  // Retry loop: maximum `retries` attempts
  for (let attempt = 1; attempt <= retries; attempt++) {
    // 🟡 Inform user UI that a retry is happening
    store.dispatch(
      setStreamingStatusMessages(`Connection lost. Retrying (${attempt}/3)...`)
    );

    // Wait between retries (default: 5 seconds)
    await wait(delay);

    // Declare a safe conversation ID
    let conversationIdToUse: string | undefined;

    if (isNewChat) {
      // 🔄 New chat: fetch latest chat from backend to identify the missing conversation
      const historyResponse = await tara_chat_history_api_call(1, 1);
      const latestChat = historyResponse?.data?.[0];

      // Skip retry if no valid chat ID was returned
      if (
        !latestChat ||
        typeof latestChat !== 'object' ||
        typeof latestChat.conversation_id !== 'string'
      ) {
        continue;
      }

      conversationIdToUse = latestChat.conversation_id;

      // Check if this chat already exists in Redux history
      const isAlreadyInHistory = chatHistoryList.some(
        (chat) => chat.chatId === conversationIdToUse
      );

      // If already in history → something went wrong → retry
      if (isAlreadyInHistory) continue;
    } else {
      // ✅ Existing chat: use current chatId directly
      if (typeof chatId !== 'string' || !chatId.startsWith('TaraAi_')) continue;
      conversationIdToUse = chatId;
    }

    // ⛔ Safety check to prevent undefined conversationId usage
    if (typeof conversationIdToUse !== 'string' || !conversationIdToUse) {
      continue;
    }

    // 📡 Fetch full chat messages using single chat API
    const singleChat = await get_tara_single_chat_api_call(conversationIdToUse);

    // Defensive checks on returned chat structure
    if (
      !singleChat ||
      typeof singleChat !== 'object' ||
      !Array.isArray(singleChat.chat)
    ) {
      continue;
    }

    const chatBlocks = singleChat.chat;

    // Look for the last "system" role message (most reliable fallback)
    const lastSystemMsg = [...chatBlocks]
      .reverse()
      .find((msg) => msg && typeof msg === 'object' && msg.role === 'system');

    if (!lastSystemMsg || !lastSystemMsg.content) continue;

    const content = lastSystemMsg.content;
    const responseId = lastSystemMsg.response_id || null;

    // ✅ Check if the message is valid content
    const isValidContent =
      typeof content === 'string' ||
      Array.isArray(content) ||
      (typeof content === 'object' && content !== null);

    if (isValidContent) {
      // Stringify object content if needed (for consistency)
      const finalMessage =
        typeof content === 'object' && !Array.isArray(content)
          ? JSON.stringify(content)
          : content;

      // 🧹 Clear streaming retry message from UI after a delay
      setTimeout(() => {
        store.dispatch(setStreamingStatusMessages(''));
      }, 5000);

      // ✅ Return fallback success response in standard format
      return {
        success: true,
        conversation_id: conversationIdToUse,
        message: finalMessage,
        debug_data: null,
        aborted: false,
        error: null,
        response_id: responseId,
      };
    }
  }

  // ❌ All retry attempts failed — return error
  return {
    success: false,
    message: 'Streaming error occurred. Please try again.',
    error: 'streaming_retry_failed',
  };
}

/* ----------------Helper Function End ---------------------*/

export default async function tara_chat_stream_api_call(
  message: string,
  signal?: AbortSignal
) {
  const shopSlug = store.getState().user?.company?.shop_slug;
  const accessToken = store.getState().auth.authorization.accessToken;
  let sellerToken = accessToken?.sellerToken;

  const currentChat = store.getState().chat.currentChat;
  const showGraphsOnly = store.getState().chat.showGraphsOnly;
  const showDeepInsights = store.getState().chat.showDeepInsights;
  const previousResponseId = store.getState().chat.previousResponseId;
  const currentChatInfo = store.getState().chat.currentChatInfo;
  const selectedOpenAiModel = store.getState().chat.selectedOpenAiModel;
  const lastUsedMCPs = store.getState().chat.lastUsedMCPs;
  let finalResponseId: string | null = null;

  let body: any = {
    message: message,
    debug: APP_ENV === 'DEV',
    shop_slug: shopSlug,
    mixed_content: true,
    openai_model: selectedOpenAiModel,
    used_mcps: lastUsedMCPs?.length > 0 ? lastUsedMCPs : undefined,
  };

  if (currentChat?.chatId && currentChat.chatMessages?.length > 1) {
    body.conversation_id = currentChat.chatId;
  }

  if (showGraphsOnly) body.show_graphs_only = 'true';
  if (showDeepInsights) body.enable_clarification = 'true';
  if (previousResponseId && isNotEmptyString(previousResponseId)) {
    body.previous_response_id = previousResponseId;
  }

  try {
    const response = await fetch(TARA_CHAT_STREAM_API, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        Authorization: accessToken.type + ' ' + sellerToken,
      },
      body: JSON.stringify(body),
      signal,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('No reader available');
    }

    const decoder = new TextDecoder();
    let buffer = '';
    let conversationId = '';
    let accumulatedMessage: any = '';
    let debugData = null;
    let doneReceived = false;

    // New variables for handling event/data pairs
    let currentEvent: string | null = null;
    let currentData: string | null = null;

    while (!doneReceived) {
      const { done, value } = await reader.read();
      if (done) {
        doneReceived = true;
      }

      buffer += decoder.decode(value || new Uint8Array(), { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('event: ')) {
          currentEvent = line.slice(7).trim();
        } else if (line.startsWith('data: ')) {
          currentData = line.slice(6).trim();
        } else if (line.trim() === '') {
          // Blank line means event/data is complete
          if (!currentData) {
            currentEvent = null;
            continue;
          }

          try {
            const parsed = JSON.parse(currentData);

            // ✅ Handle streaming status messages
            if (
              typeof parsed?.message === 'string' &&
              isNotEmptyString(parsed?.message) &&
              parsed?.success === undefined &&
              !parsed?.message.includes('_')
            ) {
              store.dispatch(setStreamingStatusMessages(parsed?.message));
            }

            // ✅ Handle success inside event: complete OR standard stream
            if (parsed?.success === true) {
              if (parsed?.data?.conversation_id && !conversationId) {
                conversationId = parsed.data.conversation_id;
              }

              if (parsed?.data?.system_message) {
                const responseId = parsed.data.response_id;
                if (responseId && isNotEmptyString(responseId)) {
                  store.dispatch(setPreviousResponseId(responseId));
                  finalResponseId = responseId;
                }
                accumulatedMessage = parsed.data.system_message;
              }

              if (parsed?.data?.debug_data) {
                debugData = parsed.data.debug_data;
              }

              if (
                parsed.done ||
                parsed.is_final ||
                currentEvent === 'complete'
              ) {
                if (
                  parsed?.data?.tokens?.total_tokens &&
                  typeof parsed?.data?.tokens?.total_tokens === 'number'
                ) {
                  store.dispatch(
                    setCurrentChatInfo({
                      chatTokenCount:
                        currentChatInfo?.chatTokenCount +
                        parsed?.data?.tokens?.total_tokens,
                    })
                  );
                }
                if (
                  parsed?.data?.used_mcps &&
                  Array.isArray(parsed?.data?.used_mcps)
                ) {
                  store.dispatch(setLastUsedMCPs(parsed?.data?.used_mcps));
                }
                doneReceived = true;
                break;
              }
            }

            // updated block: log all except stream errors
            else if (parsed?.success === false) {
              const errorType = parsed?.error;

              if (errorType !== 'streaming_error') {
                post_tara_error_log_api_call({
                  source: 'stream_response_error',
                  conversation_id:
                    conversationId || currentChat?.chatId || null,
                  shop_slug: shopSlug,
                  // if message is a string and length is greater than 4500 characters truncate it
                  // to avoid exceeding the log size limit
                  request_message:
                    typeof message === 'string' && message.length > 4500
                      ? message.slice(0, 4500)
                      : message,
                  previous_response_id: previousResponseId || null,
                  debug_flags: {
                    showGraphsOnly,
                    showDeepInsights,
                  },
                  response_payload: parsed,
                  timestamp: new Date().toISOString(),
                  browser_info: navigator.userAgent,
                  environment: APP_ENV,
                });
              }

              let toastMessage = '';
              let messageType: 'error' | 'warning' = 'error';

              if (parsed.message && typeof parsed.message === 'string') {
                toastMessage = parsed.message;
                if (errorType === 'tokens_limit_exceed') {
                  messageType = 'warning';
                  setTimeout(() => {
                    store.dispatch(setIsPaymentPopupOpen(true));
                  }, 1000);
                }
              } else {
                switch (errorType) {
                  case 'no_valid_credentials':
                    toastMessage =
                      'No valid credentials found. Please log in again.';
                    break;
                  case 'server_connection_error':
                    toastMessage =
                      "Can't connect to the server. Please check your internet connection.";
                    break;
                  case 'tokens_limit_exceed':
                    toastMessage =
                      "You've reached the token limit. Try again later or upgrade your plan.";
                    messageType = 'warning';
                    setTimeout(() => {
                      store.dispatch(setIsPaymentPopupOpen(true));
                    }, 1000);
                    break;
                  case 'execution_error':
                    toastMessage =
                      'Something went wrong during execution. Please try again.';
                    break;
                  case 'streaming_error':
                    toastMessage =
                      'Streaming interrupted. Please refresh or try again.';
                    break;
                  case 'setup_error':
                    toastMessage =
                      'Setup failed. Please review your settings and try again.';
                    break;
                  default:
                    toastMessage = 'Something went wrong. Please try again.';
                }
              }

              store.dispatch(
                setMessage({ message: toastMessage, messageType })
              );
              return {
                success: false,
                conversation_id: null,
                message: toastMessage,
                debug_data: null,
                aborted: false,
                error: errorType || 'ai_error',
                response_id: parsed?.response_id ?? null,
                trace_id: parsed?.trace_id ?? null,
              };
            }
          } catch (e) {
            console.error('Error parsing SSE event data:', e);
          }

          // Reset after handling a message block
          currentEvent = null;
          currentData = null;
        }
      }
    }

    // Final flush of buffer if any
    if (buffer.trim().startsWith('data: ')) {
      try {
        const finalData = JSON.parse(buffer.slice(6).trim());
        if (finalData?.data?.system_message) {
          accumulatedMessage = finalData.data.system_message;
        }
      } catch (e) {
        console.warn('Final buffer parse failed:', e);
      }
    }

    await check_tara_can_chat_status_api_call();

    if (
      typeof accumulatedMessage === 'string' ||
      Array.isArray(accumulatedMessage) ||
      typeof accumulatedMessage === 'object'
    ) {
      return {
        success: true,
        conversation_id: conversationId,
        message: accumulatedMessage,
        debug_data: debugData,
        aborted: false,
        error: null,
        response_id: finalResponseId,
      };
    }

    return {
      success: false,
      conversation_id: conversationId,
      message: 'Unexpected response format.',
      debug_data: debugData,
      aborted: false,
      error: 'unexpected_format',
      response_id: finalResponseId,
    };
  } catch (error: any) {
    // 🛑 Case: User manually aborted the request
    if (error.name === 'AbortError') {
      // Log the abort event for monitoring
      post_tara_error_log_api_call({
        source: 'user_aborted',
        conversation_id: currentChat?.chatId || 'unknown',
        shop_slug: shopSlug,
        // if message is a string and length is greater than 4500 characters truncate it
        // to avoid exceeding the log size limit
        request_message:
          typeof message === 'string' && message.length > 4500
            ? message.slice(0, 4500)
            : message,
        timestamp: new Date().toISOString(),
        browser_info: navigator.userAgent,
        environment: APP_ENV,
      });

      return { success: false, aborted: true };
    }

    const retryResult = await tryFetchLatestChatMessageWithRetries();

    if (retryResult.success) {
      return retryResult;
    }

    // ✅ Log error only after retry failure of streaming
    if (retryResult.error === 'streaming_retry_failed') {
      post_tara_error_log_api_call({
        source: 'streaming_retry_failed',
        conversation_id: currentChat?.chatId || 'unknown',
        shop_slug: shopSlug,
        // if message is a string and length is greater than 4500 characters truncate it
        // to avoid exceeding the log size limit
        request_message:
          typeof message === 'string' && message.length > 4500
            ? message.slice(0, 4500)
            : message,
        previous_response_id: previousResponseId || 'unknown',
        debug_flags: {
          showGraphsOnly,
          showDeepInsights,
        },
        timestamp: new Date().toISOString(),
        browser_info: navigator.userAgent,
        environment: APP_ENV,
      });
    }

    store.dispatch(setStreamingStatusMessages(retryResult.message));

    // Auto-clear the retry error message after 5 seconds
    setTimeout(() => {
      store.dispatch(setStreamingStatusMessages(''));
    }, 5000);

    // Final return on total failure
    return {
      success: false,
      aborted: false,
      error: 'streaming_retry_failed',
      message: retryResult.message,
      conversation_id: null,
      debug_data: null,
      response_id: finalResponseId,
    };
  }
}
